import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg, fmtDate } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { PageHeader, Panel, MatchRow, Empty, Spinner, Avatar, Table } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { TeamLogoCard } from "../../components/TeamLogoCard";
import { MatchTabs } from "../PortalHome";

export function CoachTeams() {
  const { me } = useAuth();
  const ids = me.memberships.filter((m) => m.role === "coach" && m.status === "active").flatMap((m) => m.team_ids || []);
  const [players, setPlayers] = useState(null);
  const [teams, setTeams] = useState([]);
  useEffect(() => {
    Promise.all(ids.map((t) => api.get("/players", { params: { team_id: t } }))).then((rs) => setPlayers(rs.flatMap((r) => r.data)));
    api.get("/teams").then((r) => setTeams(r.data.filter((team) => ids.includes(team.id))));
  }, []); // eslint-disable-line
  if (!players) return <Spinner />;
  return (
    <div>
      <PageHeader title="Squad" sub="Player availability and status"><Link to="/coach/tactics"><Button data-testid="open-tactics-btn">Open Tactical Board</Button></Link></PageHeader>
      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="coach-team-logos">
        {teams.map((team) => <TeamLogoCard key={team.id} team={team} onChange={(updated) => setTeams((rows) => rows.map((row) => row.id === updated.id ? updated : row))} />)}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6" data-testid="squad-grid">
        {players.map((p) => (
          <div key={p.id} className="tro-card flex flex-col items-center p-4 text-center">
            <Avatar player={p} size={72} /><div className="mt-2 font-extrabold">#{p.number} {p.short_name}</div>
            <div className="text-xs text-[#6C757D]">{p.position}</div>
            <div className={`mt-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.suspended ? "bg-[#F02030] text-white" : "bg-[#E6E9ED]"}`}>{p.suspended ? "Suspended" : p.registration_status}</div>
          </div>))}
      </div>
    </div>
  );
}

export function CoachMatches({ player }) {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/matches", { params: { scope: "mine" } }).then((r) => setList(r.data)); }, []);
  if (!list) return <Spinner />;
  if (player) return <MatchTabs list={list} testid="player-my-matches" title="My Matches" sub="Your team's live matches, fixtures and results" />;
  return (
    <div>
      <PageHeader title={player ? "Fixtures" : "Match Day"} sub="Fixtures, live matches and results" />
      <div className="space-y-2">{list.length ? list.map((m) => <MatchRow key={m.id} m={m} to={player ? `/matches/${m.id}` : `/coach/tactics?match=${m.id}`} />) : <Empty />}</div>
    </div>
  );
}

export function Training({ club }) {
  const { me } = useAuth();
  const clubId = me.memberships.find((m) => m.role === "club_admin" && m.status === "active")?.org_id;
  const coachTeams = me.memberships.filter((m) => m.role === "coach" && m.status === "active").flatMap((m) => m.team_ids || []);
  const [teams, setTeams] = useState([]);
  const [list, setList] = useState(null);
  const [f, setF] = useState({ team_id: "", title: "", date: "", time: "10:00", location: "", focus: "" });
  const load = () => api.get("/training", { params: club ? { club_id: clubId } : {} }).then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/teams", { params: club ? { club_id: clubId } : {} }).then((r) => setTeams(club ? r.data : r.data.filter((t) => coachTeams.includes(t.id))));
  }, []); // eslint-disable-line
  if (!list) return <Spinner />;
  const tn = (id) => teams.find((t) => t.id === id)?.name || "";
  return (
    <div className="space-y-6">
      <PageHeader title="Training" sub="Training calendar and sessions" />
      <Panel title="Schedule session" testid="training-form">
        <div className="grid gap-2 md:grid-cols-6">
          <select data-testid="training-team" value={f.team_id} onChange={(e) => setF({ ...f, team_id: e.target.value })} className="h-10 rounded-md border px-2 text-sm"><option value="">Team</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
          <Input data-testid="training-title" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <Input data-testid="training-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
          <Input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} />
          <Input data-testid="training-location" placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
          <Button data-testid="training-submit" onClick={async () => { try { await api.post("/training", f); toast.success("Session scheduled"); load(); } catch (e) { toast.error(errMsg(e)); } }}>Add</Button>
        </div>
      </Panel>
      <Table testid="training-table" cols={[["Date", (r) => `${r.date} ${r.time}`], ["Team", (r) => tn(r.team_id)], ["Session", (r) => r.title], ["Location", (r) => r.location]]} rows={list} empty="No sessions scheduled." />
    </div>
  );
}
export { fmtDate };
